import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Loader2, Send, MessageSquare, Search, Package, User } from "lucide-react";
import { api, apiError, fmtDateTime } from "../../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

// Messagerie client↔vendeur réutilisable. mode = "client" | "vendor".
export function Messenger({ mode = "client" }) {
  const isVendor = mode === "vendor";
  const base = isVendor ? "/admin/messages/conversations" : "/messages/conversations";
  const [convs, setConvs] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [thread, setThread] = useState(null); // {conversation, messages}
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [clientFilter, setClientFilter] = useState("");
  const scrollRef = useRef(null);
  const activeRef = useRef(null);
  activeRef.current = activeId;

  const loadConvs = useCallback(async () => {
    try {
      const params = isVendor && clientFilter.trim() ? { client: clientFilter.trim() } : {};
      const { data } = await api.get(base, { params });
      setConvs(data);
    } catch (e) { /* silencieux */ } finally { setLoading(false); }
  }, [base, isVendor, clientFilter]);

  const openConv = useCallback(async (cid) => {
    setActiveId(cid);
    try {
      const { data } = await api.get(`${base}/${cid}`);
      setThread(data);
      loadConvs();
    } catch (e) { toast.error(apiError(e)); }
  }, [base, loadConvs]);

  useEffect(() => { loadConvs(); }, [loadConvs]);

  // Polling léger : rafraîchit la liste et le fil ouvert toutes les 8 s.
  useEffect(() => {
    const t = setInterval(async () => {
      loadConvs();
      const cid = activeRef.current;
      if (cid) {
        try { const { data } = await api.get(`${base}/${cid}`); setThread(data); } catch { /* */ }
      }
    }, 8000);
    return () => clearInterval(t);
  }, [base, loadConvs]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [thread]);

  const send = async () => {
    if (!body.trim() || !activeId) return;
    setSending(true);
    try {
      await api.post(`${base}/${activeId}`, { body: body.trim() });
      setBody("");
      await openConv(activeId);
    } catch (e) { toast.error(apiError(e, "Envoi impossible")); } finally { setSending(false); }
  };

  const unreadOf = (c) => (isVendor ? c.unread_vendor : c.unread_client) || 0;

  return (
    <div className="grid gap-4 md:grid-cols-[320px_1fr]" data-testid={`messenger-${mode}`}>
      {/* Liste des conversations */}
      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-100 p-3">
          <p className="flex items-center gap-2 text-sm font-semibold text-navy"><MessageSquare size={15} /> Conversations</p>
          {isVendor && (
            <div className="relative mt-2">
              <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
              <Input value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}
                placeholder="Filtrer par client / email" data-testid="messenger-client-filter"
                className="h-9 pl-8 text-sm" />
            </div>
          )}
        </div>
        <div className="max-h-[560px] overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="animate-spin text-ciel" size={20} /></div>
          ) : convs.length === 0 ? (
            <p className="p-6 text-center text-xs text-slate-400" data-testid="messenger-empty">Aucune conversation.</p>
          ) : convs.map((c) => (
            <button key={c.conversation_id} data-testid={`conv-item-${c.conversation_id}`}
              onClick={() => openConv(c.conversation_id)}
              className={`flex w-full flex-col gap-0.5 border-b border-slate-50 p-3 text-left transition-colors hover:bg-slate-50 ${activeId === c.conversation_id ? "bg-ciel/5" : ""}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 truncate text-[13px] font-semibold text-navy">
                  {c.scope === "product" ? <Package size={13} className="shrink-0 text-ciel" /> : <User size={13} className="shrink-0 text-slate-400" />}
                  {isVendor ? c.client_name || c.client_email : c.subject}
                </span>
                {unreadOf(c) > 0 && (
                  <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-ciel px-1.5 font-mono text-[10px] font-semibold text-white">{unreadOf(c)}</span>
                )}
              </div>
              <span className="truncate text-[11px] text-slate-500">{isVendor ? c.subject : c.product_title || "Question générale"}</span>
              <span className="truncate text-[11px] text-slate-400">{c.last_preview}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Fil de discussion */}
      <div className="flex h-[620px] flex-col rounded-xl border border-slate-200 bg-white">
        {!thread ? (
          <div className="flex flex-1 items-center justify-center text-sm text-slate-400" data-testid="messenger-no-thread">
            Sélectionnez une conversation pour afficher les messages.
          </div>
        ) : (
          <>
            <div className="border-b border-slate-100 p-3">
              <p className="text-sm font-semibold text-navy">{thread.conversation.subject}</p>
              <p className="text-[11px] text-slate-400">
                {isVendor ? `${thread.conversation.client_name} · ${thread.conversation.client_email}` : (thread.conversation.product_title || "Question générale")}
                {thread.conversation.order_id ? ` · Commande ${thread.conversation.order_id}` : ""}
              </p>
            </div>
            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="messenger-messages">
              {thread.messages.map((m) => {
                const mine = (isVendor && m.sender_role === "vendor") || (!isVendor && m.sender_role === "client");
                return (
                  <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[78%] rounded-2xl px-4 py-2 text-sm ${mine ? "bg-navy text-white" : "bg-slate-100 text-slate-800"}`}>
                      <p className="whitespace-pre-wrap break-words">{m.body}</p>
                      <p className={`mt-1 text-[10px] ${mine ? "text-white/60" : "text-slate-400"}`}>
                        {m.sender_role === "vendor" ? "Vendeur" : (m.sender_name || "Client")} · {fmtDateTime(m.created_at)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex items-end gap-2 border-t border-slate-100 p-3">
              <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={2}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Écrivez votre message…" data-testid="messenger-input" className="resize-none text-sm" />
              <Button onClick={send} disabled={sending || !body.trim()} data-testid="messenger-send"
                className="h-10 shrink-0 rounded-full bg-ciel px-4 text-white hover:bg-ciel/90">
                {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
